import { useCallback, useEffect, useState } from 'react'

export const isWindow = (): boolean => {
  return typeof window !== 'undefined'
}

const isBrowser = isWindow()
const isServer = !isBrowser

export type BreakpointKey = 'sm' | 'md' | 'lg' | 'xl' | '2xl'

export const BREAKPOINTS: Record<BreakpointKey, number> = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const

export type BreakpointState = Record<BreakpointKey, boolean>

const calculateBreakpoints = (): BreakpointState => {
  const value: BreakpointState = {
    sm: false,
    md: false,
    lg: false,
    xl: false,
    '2xl': false,
  }

  if (isBrowser) {
    const width = window.innerWidth

    ;(Object.keys(BREAKPOINTS) as BreakpointKey[]).forEach(breakpoint => {
      value[breakpoint] = width >= BREAKPOINTS[breakpoint]
    })
  }

  return value
}

export const useBreakpoints = (): BreakpointState => {
  const [breakpoints, setBreakpoints] = useState<BreakpointState>(calculateBreakpoints())

  const handleResize = useCallback(() => {
    setBreakpoints(calculateBreakpoints())
  }, [])

  useEffect(() => {
    if (!isBrowser) return

    // Set initial breakpoints
    setBreakpoints(calculateBreakpoints())

    // Add event listener with passive option for better performance
    window.addEventListener('resize', handleResize, { passive: true })

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [handleResize])

  return breakpoints
}

export const useIsMobile = (): boolean => {
  const breakpoints = useBreakpoints()

  if (isServer) return false

  return !breakpoints.md
}

// Additional utility hooks for specific breakpoints
export const useIsTablet = (): boolean => {
  const breakpoints = useBreakpoints()

  if (isServer) return false

  return !breakpoints.md || !breakpoints.lg
}

export const useIsDesktop = (): boolean => {
  const breakpoints = useBreakpoints()

  if (isServer) return false

  return breakpoints.lg
}

export const useIsLargeScreen = (): boolean => {
  const breakpoints = useBreakpoints()

  if (isServer) return false

  return breakpoints.xl
}

// Utility function to get current breakpoint name
export const useCurrentBreakpoint = (): BreakpointKey | null => {
  const breakpoints = useBreakpoints()

  if (isServer) return null

  const breakpointEntries = Object.entries(breakpoints) as [BreakpointKey, boolean][]
  const activeBreakpoint = breakpointEntries
    .filter(([, isActive]) => isActive)
    .sort(([a], [b]) => BREAKPOINTS[b] - BREAKPOINTS[a])[0]

  return activeBreakpoint?.[0] || null
}
